import React, { useContext, useRef, useState } from 'react';
import { Card, CardContent } from '../../../lib/card';
import {
  CheckSquare,
  SquareLibrary,
  ClipboardList,
  Folder,
  FileSearch,
  GitBranch,
  ArrowRight,
  LucideIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import { ProjectContext } from '../../../context/projectContext';
import {
  AccountFeatureFlag,
  DOCTYPE,
  Projects
} from '../../../lib/models/interfaces';
import {
  Box,
  Button,
  Modal,
  SpaceBetween
} from '@cloudscape-design/components';
import { UserContext } from '../../../context/authContext';

type DeviationProjects = {
  policy: Projects[];
  contract: Projects[];
};

type DeviationModalStep = 'action' | 'contract-projects' | 'policy-projects';

type QuickStartCard = {
  targetFlag: AccountFeatureFlag;
  title: string;
  description: string;
  bullets: string[];
  icon: LucideIcon;
  onClick: () => void;
};

const emptyDeviationProjects: DeviationProjects = {
  policy: [],
  contract: []
};

export const LandingPageNew = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const { mainProjects } = useContext(ProjectContext);
  const { companyAccount } = useContext(UserContext);
  const [currentProjects, setCurrentProjects] = useState<DeviationProjects>(
    emptyDeviationProjects
  );

  // state
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [deviationModalStep, setDeviationModalStep] =
    useState<DeviationModalStep>('action');

  const closeDeviationModal = () => {
    setModalVisible(false);
    setDeviationModalStep('action');
  };

  const openExistingProject = (projectId: string) => {
    closeDeviationModal();
    navigate(`/app/projects/${projectId}/folder/`);
  };

  const openNewContractProject = () => {
    closeDeviationModal();
    navigate('/app/projects?showNew=true&type=contract');
  };

  const openNewPolicyProject = () => {
    closeDeviationModal();
    navigate('/app/projects?showNew=true&type=policy_document');
  };

  const deviationNavigation = () => {
    const reducedProjects = mainProjects.reduce(
      (acc, curr) => {
        if (curr.projectType === DOCTYPE.policy_document) {
          acc.policy.push(curr);
        }
        if (curr.projectType === DOCTYPE.contract) {
          acc.contract.push(curr);
        }
        return acc;
      },
      { policy: [], contract: [] } as {
        policy: Projects[];
        contract: Projects[];
      }
    );
    setCurrentProjects(reducedProjects);
    setDeviationModalStep('action');
    setModalVisible(true);
  };

  const sortedPolicyProjects = [...currentProjects.policy].sort((a, b) =>
    a.projectName.localeCompare(b.projectName)
  );
  const sortedContractProjects = [...currentProjects.contract].sort((a, b) =>
    a.projectName.localeCompare(b.projectName)
  );
  const hasPolicyProjects = sortedPolicyProjects.length > 0;
  const hasContractProjects = sortedContractProjects.length > 0;
  const quickStartCards: QuickStartCard[] = [
    {
      targetFlag: 'analysis',
      title: 'Create Document Project',
      description: 'Upload your documents to analyze them with AI.',
      bullets: [
        'Extract requirements, tasks, risks',
        'Summarize multiple documents',
        'Save AI findings for reuse'
      ],
      icon: SquareLibrary,
      onClick: () => navigate('/app/projects?showNew=true&type=workspace')
    },
    {
      targetFlag: 'rfp',
      title: 'Run GAP Assessment',
      description:
        'Generate and autocomplete a standards-based questionnaire using your documents.',
      bullets: [
        'Select a compliance standard',
        'Choose your project documents',
        'AI completes the questionnaire'
      ],
      icon: ClipboardList,
      onClick: () => navigate('/app/gapanalysis')
    },
    {
      targetFlag: 'rfp',
      title: 'Start Questionnaire Project',
      description:
        'Import a spreadsheet questionnaire and let AI help generate accurate answers.',
      bullets: [
        'Upload Excel questionnaires',
        'Auto-generate draft responses',
        'Review, refine and export'
      ],
      icon: Folder,
      onClick: () =>
        navigate('/app/projects?showNew=true&type=information_request')
    },
    {
      targetFlag: 'analysis',
      title: 'Search your documents',
      description:
        'Ask questions and explore information across your projects or the global library.',
      bullets: [
        'Ask targeted questions',
        'Compare documents',
        'Discover related insights'
      ],
      icon: FileSearch,
      onClick: () => navigate('/app/search')
    },
    {
      targetFlag: 'contract',
      title: 'Contract Deviation',
      description: 'Create profiles and compare contracts.',
      bullets: ['Create profiles', 'Compare contracts', 'Highlight deviations'],
      icon: GitBranch,
      onClick: deviationNavigation
    }
  ];
  const visibleQuickStarts = quickStartCards.filter((card) =>
    companyAccount?.accountFlags?.includes(card.targetFlag)
  );
  const quickStartCount = visibleQuickStarts.length;
  const useCompactQuickStartLayout =
    quickStartCount > 0 && quickStartCount <= 2;
  const quickStartGridClass =
    quickStartCount === 1
      ? 'mx-auto grid max-w-2xl grid-cols-1 gap-6'
      : quickStartCount === 2
        ? 'mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2'
        : 'grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

  const renderQuickStartCard = (card: QuickStartCard) => {
    const Icon = card.icon;
    const featuredCard = quickStartCount === 1;

    return (
      <Card
        key={card.title}
        className={`group flex h-full cursor-pointer flex-col overflow-hidden border border-slate-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:border-slate-300 hover:shadow-lg ${
          featuredCard ? 'rounded-[28px]' : 'rounded-2xl'
        }`}
        onClick={card.onClick}
      >
        <CardContent className="flex h-full flex-col p-6">
          <div className="mb-5 flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 ring-1 ring-blue-100">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h3
                className={`font-semibold text-slate-900 ${
                  featuredCard ? 'text-2xl' : 'text-xl'
                }`}
              >
                {card.title}
              </h3>
              <p className="mt-2 max-w-[34ch] text-sm leading-6 text-slate-600">
                {card.description}
              </p>
            </div>
          </div>
          <ul className="flex-1 space-y-3 text-sm text-slate-600">
            {card.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2">
                <CheckSquare className="mt-1 h-5 w-5 shrink-0 text-green-500" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-4 text-sm font-medium text-slate-800">
            <span>Open quick start</span>
            <ArrowRight className="h-4 w-4 transition duration-200 group-hover:translate-x-1" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderProjectList = (
    projects: Projects[],
    emptyMessage: string,
    buttonText: string
  ) => {
    if (projects.length === 0) {
      return <Box color="text-body-secondary">{emptyMessage}</Box>;
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        {projects.map((project) => (
          <button
            key={project._id}
            type="button"
            onClick={() => openExistingProject(project._id)}
            className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <p className="text-base font-semibold text-gray-900">
              {project.projectName}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {project.projectDescription || 'No project description'}
            </p>
            <p className="mt-3 text-sm font-medium text-blue-700">
              {buttonText}
            </p>
          </button>
        ))}
      </div>
    );
  };

  const renderActionButton = (props: {
    title: string;
    description: string;
    buttonLabel: string;
    disabled?: boolean;
    onClick: () => void;
  }) => {
    return (
      <button
        type="button"
        disabled={props.disabled}
        onClick={props.onClick}
        className={`w-full rounded-lg border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
          props.disabled
            ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
            : 'border-gray-200 bg-white hover:border-blue-500 hover:bg-blue-50'
        }`}
      >
        <p className="text-base font-semibold">{props.title}</p>
        <p className="mt-2 text-sm">{props.description}</p>
        <p className="mt-3 text-sm font-medium text-blue-700">
          {props.buttonLabel}
        </p>
      </button>
    );
  };

  const renderDeviationModalContent = () => {
    switch (deviationModalStep) {
      case 'contract-projects':
        return (
          <SpaceBetween size="m">
            <Box color="text-body-secondary">
              Select the contract project you want to open for comparison.
            </Box>
            {renderProjectList(
              sortedContractProjects,
              'No contract projects are available yet.',
              'Open contract project'
            )}
            <Button variant="normal" onClick={openNewContractProject}>
              Create New Contract Project
            </Button>
          </SpaceBetween>
        );
      case 'policy-projects':
        return (
          <SpaceBetween size="m">
            <Box color="text-body-secondary">
              Select the policy project where you want to add a new policy.
            </Box>
            {renderProjectList(
              sortedPolicyProjects,
              'No policy projects are available yet.',
              'Open policy project'
            )}
            <Button variant="normal" onClick={openNewPolicyProject}>
              Create New Policy Project
            </Button>
          </SpaceBetween>
        );
      default:
        return (
          <SpaceBetween size="m">
            <div>
              <p className="text-base font-semibold text-gray-900">
                {hasPolicyProjects && hasContractProjects
                  ? 'What do you want to do?'
                  : 'Choose the next step for contract deviation.'}
              </p>
              <p className="mt-2 text-sm text-gray-600">
                Policy projects: {sortedPolicyProjects.length} | Contract
                projects: {sortedContractProjects.length}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {renderActionButton({
                title: 'Compare A Contract',
                description:
                  'Choose an existing contract project and continue from there.',
                buttonLabel: hasContractProjects
                  ? 'Choose contract project'
                  : 'No contract projects yet',
                disabled: !hasContractProjects,
                onClick: () => setDeviationModalStep('contract-projects')
              })}
              {renderActionButton({
                title: 'Create A New Policy',
                description:
                  'Open one of your existing policy projects and add the new policy there.',
                buttonLabel: hasPolicyProjects
                  ? 'Choose policy project'
                  : 'No policy projects yet',
                disabled: !hasPolicyProjects,
                onClick: () => setDeviationModalStep('policy-projects')
              })}
              {renderActionButton({
                title: 'Create A New Policy Project',
                description:
                  'Start a new policy-document project before adding policies.',
                buttonLabel: 'Create policy project',
                onClick: openNewPolicyProject
              })}
            </div>
            {!hasContractProjects ? (
              <Box color="text-status-warning">
                You need at least one contract project before you can compare a
                contract.
              </Box>
            ) : null}
            {!hasPolicyProjects ? (
              <Box color="text-status-warning">
                You need at least one policy project before you can add a new
                policy to an existing project.
              </Box>
            ) : null}
          </SpaceBetween>
        );
    }
  };
  return (
    <div
      className="relative h-screen overflow-y-auto"
      ref={containerRef}
      style={{ scrollbarWidth: 'none' }}
    >
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-6">
                AI Powered Compliance & Document Analysis.
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Understand your documents, answer questionnaires and generate
                assessments - all in one platform.
              </p>
            </div>
          </div>
        </div>
        {/* Quick Start section */}
        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-slate-500">
                Get moving
              </p>
              <h2 className="mt-4 text-3xl font-bold text-slate-900">
                Quick starts
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Jump into the workflows available on this account.
              </p>
            </div>
            {visibleQuickStarts.length === 0 ? (
              <div className="mx-auto mt-12 max-w-2xl rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center text-slate-600">
                No quick starts are available for this account yet.
              </div>
            ) : useCompactQuickStartLayout ? (
              <div className="mx-auto mt-12 max-w-6xl rounded-[32px] border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 shadow-sm sm:p-8">
                <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)] lg:gap-8">
                  <div className="flex h-full flex-col justify-between rounded-[24px] border border-slate-200 bg-white/90 p-6">
                    <div>
                      <p className="text-sm font-medium uppercase tracking-[0.16em] text-slate-500">
                        Recommended next steps
                      </p>
                      <h3 className="mt-3 text-2xl font-semibold text-slate-900">
                        Start with the workflows your team can use right now.
                      </h3>
                      <p className="mt-3 text-sm leading-6 text-slate-600">
                        You only see quick starts that match the tools enabled
                        for this account.
                      </p>
                    </div>
                    <div className="mt-6 space-y-3 text-sm text-slate-600">
                      <div className="rounded-2xl bg-slate-50 px-4 py-3">
                        <span className="font-semibold text-slate-900">
                          {quickStartCount}
                        </span>{' '}
                        {quickStartCount === 1
                          ? 'workflow is ready.'
                          : 'workflows are ready.'}
                      </div>
                      <p>
                        Need something else? You can still browse the full
                        Projects area from the left nav.
                      </p>
                    </div>
                  </div>
                  <div className={quickStartGridClass}>
                    {visibleQuickStarts.map(renderQuickStartCard)}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`mt-12 ${quickStartGridClass}`}>
                {visibleQuickStarts.map(renderQuickStartCard)}
              </div>
            )}
          </div>
        </div>

        {/* Key Capabilities Section */}
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-center mb-12">
            What You Can Do with KORU
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Analyze Documents</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>
                      Automatically identify key obligations and requirements
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Extract and track important deadlines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Categorize information by type and priority</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <GitMerge className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Connect Information</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Link related requirements across documents</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Maintain traceability as obligations evolve</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>
                      Create a searchable web of connected information
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-8 h-8 text-blue-600" />
                  <h3 className="text-xl font-semibold">Collaborate & Share</h3>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Work together on document reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Share insights and maintain review history</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckSquare className="w-5 h-5 text-green-500 mt-1" />
                    <span>Generate focused reports for stakeholders</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="mt-12 text-center">
            <button
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 inline-flex items-center gap-2"
              onClick={() => {
                navigate('/app/projects');
              }}
            >
              Start Your Project <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div> */}

        {/* Knowledge Base Banner */}
        {/* <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-4">
                    Build Your Knowledge Base
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Create a searchable library of document expertise that grows
                    with each project. Maintain institutional knowledge and
                    ensure consistency across your organization.
                  </p>
                  <button
                    className="bg-white text-blue-600 px-6 py-2 rounded-md border border-blue-600 hover:bg-blue-50"
                    onClick={() => {
                      navigate('/app/landing/knowledgebase');
                    }}
                  >
                    Learn More
                  </button>
                </div>
                <div className="flex-1 flex justify-center">
                  <BookOpen className="w-32 h-32 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}
      </div>
      <Modal
        visible={modalVisible}
        onDismiss={closeDeviationModal}
        header={
          deviationModalStep === 'contract-projects'
            ? 'Select A Contract Project'
            : deviationModalStep === 'policy-projects'
              ? 'Select A Policy Project'
              : 'Contract Deviation'
        }
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            {deviationModalStep !== 'action' ? (
              <Button onClick={() => setDeviationModalStep('action')}>
                Back
              </Button>
            ) : null}
            <Button variant="primary" onClick={closeDeviationModal}>
              Close
            </Button>
          </SpaceBetween>
        }
      >
        {renderDeviationModalContent()}
      </Modal>
      <ScrollDownArrow
        containerRef={containerRef as React.RefObject<HTMLElement>}
        bottomValue="12%"
      />
    </div>
  );
};
